import { createAsyncThunk } from 'app/store/utils';
import axios from 'axios';
import { Link } from 'types/api.types';
import {
  ChatMessageItem,
  ChatStatus,
  CreateChatMessageResponse,
  DeleteChatMessageResponse,
  FetchChannelEffectData,
  FetchChannelMessagesResponse,
  FetchChannelsResponse,
  FetchChatUnreadCountResponse,
  GetSongChannelResponse,
  MessageType,
  UpdateLastSeenMessageResponse,
} from 'types/chat.types';
import {
  getChatChannelMessagesLink,
  getChatUnreadCountLink,
  getCreateChatMessageLink,
  getCurrentChannel,
  getCurrentChannelMessages,
  getLastSeenMessageTime,
  getListChatChannelsLink,
  getSongChannelLink,
  getUpdateLastSeenMessageLink,
} from 'app/store/Chat/selectors';
import { removeFromMessages } from 'app/store/Chat/slice';
import { buildQueryUrl, isoToTime } from 'app/utils/utils';
import { getUserGuid } from 'app/store/Auth/selectors';
import { addAlertAction } from '../Alerts/slice';

export const fetchSongChannelEffect = createAsyncThunk(
  'chat/fetchSongChannel',
  async (data: FetchChannelEffectData, { getState, dispatch }) => {
    const link = getSongChannelLink(getState());
    try {
      if (!link) {
        throw new Error('Song channel link not found');
      }

      const response = await axios.get<GetSongChannelResponse>(
        link.href.replace('{channelGuid}', data.channelGuid),
        {
          withCredentials: true,
        }
      );

      const { item } = response.data.response.channel;
      return {
        ...item,
        lastReadMessageDate: isoToTime(item.lastReadMessageDate),
      };
    } catch (e) {
      const currentChannel = getCurrentChannel(getState());
      if (currentChannel?.status !== ChatStatus.PENDING) {
        dispatch(
          addAlertAction({
            message: 'Could not get song channel for chat',
          })
        );
      }
      throw e;
    }
  }
);

export const createChatMessageEffect = createAsyncThunk<
  ChatMessageItem,
  {
    channelGuid: string;
    message: string;
    messageGuid?: string;
    refId?: string;
    isFirstMessage?: boolean;
  }
>('chat/createChatMessage', async (data, { getState, dispatch }) => {
  const link = getCreateChatMessageLink(getState());
  try {
    if (!link) {
      throw new Error('Create chat message link not found');
    }

    const response = await axios.post<CreateChatMessageResponse>(
      link.href.replace('{channelGuid}', data.channelGuid),
      {
        text: data.message,
        refId: data.refId,
      },
      {
        withCredentials: true,
      }
    );

    if (data.messageGuid && data.messageGuid.startsWith('RETRY')) {
      dispatch(removeFromMessages(data.messageGuid));
    }

    const { item } = response.data.response.message;
    return {
      ...item,
      created: isoToTime(item.created),
      updated: isoToTime(item.updated),
    };
  } catch (e) {
    dispatch(
      addAlertAction({
        message: 'Unable to send this message. Please try again later',
      })
    );
    throw e;
  }
});

export const fetchChatChannelsEffect = createAsyncThunk(
  'chat/fetchChatChannels',
  async (data: { next?: Link } | undefined = {}, { getState, dispatch }) => {
    const link = getListChatChannelsLink(getState());

    try {
      if (!link) {
        throw new Error('Chat messages list link not found');
      }
      const url = data.next?.href || buildQueryUrl(link.href, {});
      const response = await axios.request<FetchChannelsResponse>({
        url,
        method: link.method,
        withCredentials: true,
      });
      const { items, _links } = response.data.response.channel;
      return { items, _links };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to load messages. Please try again' })
      );
      throw e;
    }
  }
);

export const deleteChatMessageEffect = createAsyncThunk(
  'chat/deleteChatMessage',
  async (data: MessageType, { dispatch }) => {
    const deleteMessage = data?._links?.deleteMessage;
    try {
      if (!deleteMessage) {
        throw new Error('Delete chat message link not found');
      }

      const { data } = await axios.request<DeleteChatMessageResponse>({
        url: deleteMessage.href,
        method: deleteMessage.method,
        withCredentials: true,
      });

      const { item } = data.response.message;
      return {
        ...item,
        created: isoToTime(item.created),
        updated: isoToTime(item.updated),
      };
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Could not delete message. Please try again',
        })
      );
      throw e;
    }
  }
);

export const updateLastSeenMessageEffect = createAsyncThunk(
  'chat/updateLastSeenChatMessage',
  async (_, { dispatch, getState }) => {
    const link = getUpdateLastSeenMessageLink(getState());
    const messages = getCurrentChannelMessages(getState());
    const lastSeenMessageTime = getLastSeenMessageTime(getState());
    const userGuid = getUserGuid(getState());
    const lastMessage = messages[messages.length - 1];
    const lastMessageSentBySelf = lastMessage.createdBy?.userGuid === userGuid;

    const lastSeenMessageTimeMatch =
      lastMessage?.created?.timestamp === lastSeenMessageTime?.timestamp;

    if (lastMessageSentBySelf || lastSeenMessageTimeMatch) {
      return null;
    }

    try {
      if (!link) {
        throw new Error('Update last seen message link not found');
      }

      const lastReadMessageDate = lastMessage?.updated?.timestamp || Date.now();
      const payload = { lastReadMessageDate };
      const { data } = await axios.request<UpdateLastSeenMessageResponse>({
        url: link.href,
        method: link.method,
        withCredentials: true,
        data: payload,
      });

      return {
        lastReadMessageDate: isoToTime(
          data.response.channelUser.item.lastReadMessageDate
        ),
      };
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Could not update last seen timestamp',
        })
      );
      throw e;
    }
  }
);

export const fetchChatChannelMessagesEffect = createAsyncThunk(
  'chat/fetchChatChannelMessages',
  async (data: { next?: Link } | undefined = {}, { getState, dispatch }) => {
    const link = getChatChannelMessagesLink(getState());
    const { next } = data;
    try {
      if (!link) {
        throw new Error('Fetch channel messages link not found');
      }

      const response = await axios.get<FetchChannelMessagesResponse>(
        next?.href || link.href,
        {
          withCredentials: true,
        }
      );

      const { items, _links } = response.data.response.message;
      return {
        items: items.map((item) => ({
          ...item,
          created: isoToTime(item.created),
          updated: isoToTime(item.updated),
        })),
        _links,
        isNext: Boolean(next),
      };
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Could not fetch messages',
        })
      );
      throw e;
    }
  }
);

export const fetchChatUnreadCountEffect = createAsyncThunk(
  'chat/fetchChatUnreadCount',
  async (data, { getState }) => {
    const link = getChatUnreadCountLink(getState());

    if (!link) {
      throw new Error('Unread count link not found');
    }

    const response = await axios.request<FetchChatUnreadCountResponse>({
      url: link.href,
      method: link.method,
      withCredentials: true,
    });

    return response.data.response.unread.item.hasUnreadMessages;
  }
);
